import React, {
    memo,
    useEffect,
    useLayoutEffect,
    useMemo,
} from 'react';
import { PageLayout, useAppDispath } from 'shared';
import {
    AwardsBlock,
    Footer,
    Header,
    CrewMembersBlock,
    PortfolioBlock,
    PlacesBlock,
    FAQsBlock,
    DegustationBlock,
    ImagesBlock,
    ContactsBlock,
    NavigationBlock,
    Breadcrumb,
    Toolbar,
} from 'widgets';
import { useCart } from 'features';
import { Helmet } from 'react-helmet-async';
import { CartPageMain } from './components/cart-page-main';
import { CartPageActions } from '../model/slice/slice';
import { useCartPageState } from '../lib/hooks/use-cart-page-state';
import { ICartFullItem } from '../lib/types/cart-full-item';

const MemoBreadcrumb = memo(Breadcrumb);
const MemoFooter = memo(Footer);
const MemoAwardsBlock = memo(AwardsBlock);
const MemoPortfolioBlock = memo(PortfolioBlock);
const MemoPlacesBlock = memo(PlacesBlock);
const MemoFAQsBlock = memo(FAQsBlock);
const MemoDegustationBlock = memo(DegustationBlock);
const MemoImagesBlock = memo(ImagesBlock);
const MemoContactsBlock = memo(ContactsBlock);
const MemoNavigationBlock = memo(NavigationBlock);
const MemoMain = CartPageMain;

export const CartPage = () => {
    const dispatch = useAppDispath();

    const { items } = useCart();
    const {
        deliveryArticles,
        culinaryStudioArticles,
        outdoorBarArticles,
        sweetShopArticles,
        cakeToOrders,
    } = useCartPageState();

    useEffect(() => {
        const deliveryItems = items.filter(({ type }) => type === 'delivery');
        const culinaryStudioItems = items.filter(({ type }) => type === 'culinary-studio');
        const outdoorBarsItems = items.filter(({ type }) => type === 'outdoor-bars');
        const sweetShopItems = items.filter(({ type }) => type === 'sweet-shop');
        const cakeToOrderItems = items.filter(({ type }) => type === 'cake-to-order');

        if (deliveryItems.length > 0) {
            dispatch(CartPageActions.fetchDeliveryArticlesByUids({
                uids: deliveryItems.map(({ uid }) => uid),
            }));
        }

        if (culinaryStudioItems.length > 0) {
            dispatch(CartPageActions.fetchCulinaryStudioArticlesByUids({
                uids: culinaryStudioItems.map(({ uid }) => uid),
            }));
        }

        if (outdoorBarsItems.length > 0) {
            dispatch(CartPageActions.fetchOutdoorBarArticlesByUids({
                uids: outdoorBarsItems.map(({ uid }) => uid),
            }));
        }

        if (sweetShopItems.length > 0) {
            dispatch(CartPageActions.fetchSweetShopArticlesByUids({
                uids: sweetShopItems.map(({ uid }) => uid),
            }));
        }

        if (cakeToOrderItems.length > 0) {
            dispatch(CartPageActions.fetchCakeToOrdersByUids({
                uids: cakeToOrderItems.map(({ uid }) => uid),
            }));
        }
    }, [
        JSON.stringify(items),
    ]);

    useLayoutEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    const cartItems: (ICartFullItem| undefined)[] = useMemo(() => (
        items.map(({
            type,
            uid,
            portionIndex,
            count,
        // eslint-disable-next-line array-callback-return, consistent-return
        }) => {
            // eslint-disable-next-line default-case
            switch (type) {
            case 'delivery': {
                const article = deliveryArticles.find(({ uid: uid2 }) => uid2 === uid);
                if (article) {
                    return ({
                        type,
                        uid,
                        portionIndex,
                        count,
                        title: article.name,
                        price: article.price ? article.price * count : undefined,
                        image: article.image && {
                            url: article.image.url,
                        },
                    } as ICartFullItem);
                }
                break;
            }
            case 'culinary-studio': {
                const article = culinaryStudioArticles.find(({ uid: uid2 }) => uid2 === uid);
                if (article && portionIndex !== undefined) {
                    const title = `${article.name} – ${article.portions?.[portionIndex].value}${article.portions?.[portionIndex].meassure}`;
                    const portion = article.portions?.[portionIndex];
                    return ({
                        type,
                        uid,
                        portionIndex,
                        count,
                        title,
                        price: (
                            portion?.price
                                ? portion.price * count
                                : undefined
                        ),
                        image: article.image && {
                            url: article.image.url,
                        },
                    } as ICartFullItem);
                }
                break;
            }
            case 'outdoor-bars': {
                const article = outdoorBarArticles.find(({ uid: uid2 }) => uid2 === uid);
                if (article && portionIndex !== undefined) {
                    const title = `${article.name} – ${article.portions?.[portionIndex].value}${article.portions?.[portionIndex].meassure}`;
                    const portion = article.portions?.[portionIndex];
                    return ({
                        type,
                        uid,
                        portionIndex,
                        count,
                        title,
                        price: (
                            portion?.price
                                ? portion.price * count
                                : undefined
                        ),
                        image: article.image && {
                            url: article.image.url,
                        },
                    } as ICartFullItem);
                }
                break;
            }
            case 'sweet-shop': {
                const article = sweetShopArticles.find(({ uid: uid2 }) => uid2 === uid);
                if (article) {
                    return ({
                        type,
                        uid,
                        portionIndex,
                        count,
                        title: article.name,
                        price: article.price ? article.price * count : undefined,
                        image: article.image && {
                            url: article.image.url,
                        },
                    } as ICartFullItem);
                }
                break;
            }
            case 'cake-to-order': {
                const article = cakeToOrders.find(({ uid: uid2 }) => uid2 === uid);
                if (article && portionIndex !== undefined) {
                    const title = `${article.name} – ${article.portions?.[portionIndex].value} ${article.portions?.[portionIndex].meassure}`;
                    const portion = article.portions?.[portionIndex];
                    return ({
                        type,
                        uid,
                        portionIndex,
                        count,
                        title,
                        pricePrefix: portion?.pricePrefix,
                        price: (
                            portion?.price
                                ? portion.price * count
                                : undefined
                        ),
                        image: article.image && {
                            url: article.image.url,
                        },
                    } as ICartFullItem);
                }
                break;
            }
            default: return undefined;
            }
        })
    ), [
        deliveryArticles,
        culinaryStudioArticles,
        outdoorBarArticles,
        sweetShopArticles,
        cakeToOrders,
        JSON.stringify(items),
    ]);

    return (
        <PageLayout
            toolbar={<Toolbar />}
        >
            <Helmet>
                <title>Корзина</title>
                <meta name="description" content="Корзина магазина Corso Catering" />
                <meta property="og:title" content="Корзина магазина Corso Catering" />
            </Helmet>
            <Header />
            <MemoBreadcrumb />
            <MemoMain
                cartItems={
                    cartItems.filter(
                        (item) => item !== undefined && item.count > 0,
                    ) as ICartFullItem[]
                }
            />
            <MemoAwardsBlock />
            <MemoImagesBlock />
            <MemoDegustationBlock />
            <CrewMembersBlock />
            <MemoPortfolioBlock />
            <MemoPlacesBlock />
            <MemoContactsBlock />
            <MemoFAQsBlock />
            <MemoNavigationBlock />
            <MemoFooter />
        </PageLayout>
    );
};
